import { assertNever } from '@kofno/piper';
import { fromNullable, Maybe, nothing } from 'maybeasy';
import { ProgramCategory, programCategoryFor } from '../../../../Licensing/ProgramCategories';
import { Program, ProgramFamily, ShareState } from '../Stores/ShareStore/Types';

// One row in the catalog: a course, with every run of it the collection can
// share, earliest first. On-demand courses have a single, permanent run.
export interface CourseOption {
  key: string;
  family: ProgramFamily;
  category: ProgramCategory;
  runs: ReadonlyArray<Program>;
}

export interface CategoryGroup {
  category: ProgramCategory;
  options: ReadonlyArray<CourseOption>;
}

// The courses an admin can invite people straight into. Empty until the
// collection has loaded.
export const coursesFor = (state: ShareState): ReadonlyArray<Program> => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'error':
      return [];
    case 'ready':
    case 'searching':
    case 'reviewing':
    case 'refreshing':
    case 'publishing':
    case 'submitting':
    case 'confirming':
      return state.learningCollectionResource.payload.programs;
    default:
      return assertNever(state);
  }
};

// Course or collection is decided in the Share overlay, before this page; only
// a course invite has a course to choose here.
export const isCourseInvite = (state: ShareState): boolean => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'error':
      return false;
    case 'ready':
    case 'searching':
    case 'reviewing':
    case 'refreshing':
    case 'publishing':
    case 'submitting':
    case 'confirming':
      switch (state.shareType) {
        case 'collection':
          return false;
        case 'program':
          return true;
        default:
          return assertNever(state.shareType);
      }
    default:
      return assertNever(state);
  }
};

const programIdOf = (state: ShareState): Maybe<number> => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'error':
      return nothing();
    case 'ready':
    case 'searching':
    case 'reviewing':
    case 'refreshing':
    case 'publishing':
    case 'submitting':
    case 'confirming':
      return state.programId;
    default:
      return assertNever(state);
  }
};

// Nothing until the admin picks a course, and always for a collection invite.
export const selectedProgramId = (state: ShareState): Maybe<number> =>
  isCourseInvite(state) ? programIdOf(state) : nothing();

// A course invite with no course picked yet. Sending it would leave out the
// program, and the server would treat it as a collection invite.
export const awaitingCourse = (state: ShareState): boolean =>
  isCourseInvite(state) && selectedProgramId(state).isNothing();

const byStart = (a: Program, b: Program): number => a.startOn.getTime() - b.startOn.getTime();

// Rows for the catalog: the runs grouped under the given key, earliest first
// within a row, rows by course name.
const groupedCourseOptions = (
  programs: ReadonlyArray<Program>,
  keyOf: (program: Program) => string,
): ReadonlyArray<CourseOption> => {
  const options = new Map<string, CourseOption>();
  programs.forEach((program) => {
    const key = keyOf(program);
    const existing = fromNullable(options.get(key));
    options.set(key, {
      key,
      family: program.programFamily,
      category: programCategoryFor(program.programFamily.offeringType),
      runs: existing
        .map((option) => [...option.runs, program].sort(byStart))
        .getOrElseValue([program]),
    });
  });
  return Array.from(options.values()).sort((a, b) => a.family.name.localeCompare(b.family.name));
};

// One row per course and kind, for the invite page: the pick is a run, and a
// family offered both on demand and in scheduled runs is two different things
// to enroll in.
export const courseOptions = (programs: ReadonlyArray<Program>): ReadonlyArray<CourseOption> =>
  groupedCourseOptions(programs, (program) => `${program.programFamily.id}-${program.kind}`);

// One row per course family, every run of it merged, for sharing by URL: the
// URL points at the family, so a family offered both on demand and in
// scheduled runs is one choice, not two (DVLP-8060).
export const familyCourseOptions = (
  programs: ReadonlyArray<Program>,
): ReadonlyArray<CourseOption> =>
  groupedCourseOptions(programs, (program) => String(program.programFamily.id));

export const selectedRun = (state: ShareState): Maybe<Program> =>
  selectedProgramId(state).andThen((id) =>
    fromNullable(coursesFor(state).find((program) => program.id === id)),
  );

export const courseCount = (state: ShareState): number => courseOptions(coursesFor(state)).length;

// Premium first: leadership coaching, then professor-led, then on-demand.
// Group coaching sits last for now.
const CATALOG_ORDER: ReadonlyArray<ProgramCategory> = [
  'leadership-coaching',
  'professor-led',
  'on-demand',
  'group-coaching',
];

export const groupByCategory = (
  options: ReadonlyArray<CourseOption>,
): ReadonlyArray<CategoryGroup> =>
  CATALOG_ORDER.map((category) => ({
    category,
    options: options.filter((option) => option.category === category),
  })).filter((group) => group.options.length > 0);

export const optionFor = (
  options: ReadonlyArray<CourseOption>,
  programId: number,
): Maybe<CourseOption> =>
  fromNullable(options.find((option) => option.runs.some((run) => run.id === programId)));

// The row for a course shared by URL, among rows built by familyCourseOptions
// (one per family).
export const optionForFamily = (
  options: ReadonlyArray<CourseOption>,
  programFamilyId: number,
): Maybe<CourseOption> =>
  fromNullable(options.find((option) => option.family.id === programFamilyId));

export const firstRun = (option: CourseOption): Maybe<Program> => fromNullable(option.runs[0]);

export const matchesQuery = (option: CourseOption, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  const school = option.family.schoolName.getOrElseValue('');
  return [option.family.name, school].some((text) => text.toLowerCase().includes(needle));
};

// Several runs of one professor-led course get a run picker; one run, or an
// on-demand course, is stated rather than chosen.
export type RunChoice =
  | { kind: 'start-anytime' }
  | { kind: 'single-run'; run: Program }
  | { kind: 'several-runs'; runs: ReadonlyArray<Program> };

const isScheduled = (run: Program): boolean => {
  switch (run.kind) {
    case 'scheduled':
      return true;
    case 'on-demand':
      return false;
    default:
      return assertNever(run.kind);
  }
};

export const runChoice = (option: CourseOption): RunChoice => {
  const scheduled = option.runs.filter(isScheduled);
  if (scheduled.length === 0) {
    return { kind: 'start-anytime' };
  }
  if (scheduled.length === 1) {
    return { kind: 'single-run', run: scheduled[0] };
  }
  return { kind: 'several-runs', runs: scheduled };
};
